import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RotateCw, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, fmtTime } from "@/lib/api";

const STATE_BADGE = {
  running: { color: "text-[#00E676]", label: "RUNNING" },
  sleeping: { color: "text-[#A0A0AB]", label: "IDLE" },
  blocked: { color: "text-[#F59E0B]", label: "BLOCKED" },
  crashed: { color: "text-[#FF3B30]", label: "CRASHED" },
  stopped: { color: "text-[#71717A]", label: "STOPPED" },
};

export default function EngineHealthBanner({ compact = false }) {
  const [hb, setHb] = useState(null);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const data = await api.signalEngineHeartbeat();
        if (alive) setHb(data);
      } catch (e) {
        // ignore — don't spam
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const onRestart = async () => {
    try {
      setRestarting(true);
      await api.signalEngineRestart();
      toast.success("Signal engine restarted");
      const data = await api.signalEngineHeartbeat();
      setHb(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setRestarting(false); }
  };

  if (!hb) return null;
  const badge = STATE_BADGE[hb.state] || STATE_BADGE.stopped;
  const stale = hb.stale === true;
  const crashed = hb.state === "crashed";
  const showAlert = crashed || stale;

  if (compact && !showAlert) {
    // Tiny status pill — no banner unless something is wrong
    return (
      <div data-testid="engine-pill" className="flex items-center gap-2 text-[10px] text-[#A0A0AB] font-mono">
        <Activity size={10} strokeWidth={2} className={badge.color} />
        <span className={badge.color}>{badge.label}</span>
        <span>· cycles {hb.cycle_count}</span>
        {hb.failure_count > 0 ? <span className="text-[#F59E0B]">· {hb.failure_count} fail</span> : null}
      </div>
    );
  }

  return (
    <div
      data-testid="engine-health-banner"
      className={`rounded-sm border p-3 ${
        crashed
          ? "bg-[#FF3B30]/10 border-[#FF3B30]/40"
          : stale
            ? "bg-[#F59E0B]/10 border-[#F59E0B]/40"
            : "bg-[#0F0F14] border-border"
      }`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          {showAlert ? (
            <AlertTriangle size={14} strokeWidth={2}
              className={crashed ? "text-[#FF3B30]" : "text-[#F59E0B]"} />
          ) : (
            <Activity size={14} strokeWidth={2} className={badge.color} />
          )}
          <div className="text-xs">
            <span className="text-[10px] uppercase tracking-widest text-[#71717A] mr-2">Signal Engine</span>
            <span className={`font-semibold ${badge.color}`}>{badge.label}</span>
            {crashed ? (
              <span className="ml-2 text-[#FF3B30]">— task died, click Restart</span>
            ) : stale ? (
              <span className="ml-2 text-[#F59E0B]">heartbeat stale ({hb.stale_seconds}s &gt; {hb.stale_threshold_seconds}s)</span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-[#A0A0AB] font-mono">
          <span data-testid="engine-cycle-count">cycles {hb.cycle_count}</span>
          <span data-testid="engine-fail-count" className={hb.failure_count > 0 ? "text-[#F59E0B]" : ""}>
            failures {hb.failure_count}
          </span>
          <span>last ok: {hb.last_success_at ? fmtTime(hb.last_success_at) : "—"}</span>
          {showAlert ? (
            <Button
              data-testid="engine-restart-btn"
              size="sm"
              onClick={onRestart}
              disabled={restarting}
              className="rounded-sm bg-[#00E676] hover:bg-[#00C766] text-black font-semibold text-xs"
            >
              <RotateCw size={11} strokeWidth={2} className="mr-1" />
              {restarting ? "Restarting…" : "Restart engine"}
            </Button>
          ) : null}
        </div>
      </div>
      {hb.last_error_msg && (crashed || stale) ? (
        <div className="mt-2 text-[11px] text-[#FF3B30] font-mono break-words">
          last error: {hb.last_error_msg}
        </div>
      ) : null}
    </div>
  );
}
